import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate, Link, useParams, useLocation } from "react-router-dom";
import { API_BASE_URL } from "../utils/config";
import { FiMail, FiLock, FiArrowRight, FiEye, FiEyeOff, FiAlertCircle, FiCheckCircle } from "react-icons/fi";
import { FaTasks, FaKey, FaTrophy, FaStar, FaFire, FaVolumeUp, FaCheckCircle, FaSpinner } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";

const CandidateLogin = () => {
    const { jobId } = useParams();
    const location = useLocation();
    const [formData, setFormData] = useState({ email: "", password: "" });
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [message, setMessage] = useState({ text: "", type: "" });
    const [showCongratsPopup, setShowCongratsPopup] = useState(false);
    const [candidateName, setCandidateName] = useState("");
    const [isVoicePlaying, setIsVoicePlaying] = useState(false);
    const [autoLoginAttempted, setAutoLoginAttempted] = useState(false);
    const [autoLoginLoading, setAutoLoginLoading] = useState(false);
    const navigate = useNavigate();

    // Clear all toasts
    const clearAllToasts = () => {
        toast.dismiss();
    };

    // Navigate with toast cleanup
    const navigateWithCleanup = (path) => {
        clearAllToasts();
        navigate(path);
    };

    // Cleanup toasts on unmount
    useEffect(() => {
        return () => {
            clearAllToasts();
        };
    }, []);

    // Forgot / Reset password states
    const [step, setStep] = useState("login");
    const [forgotEmail, setForgotEmail] = useState("");
    const [resetOtp, setResetOtp] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [forgotLoading, setForgotLoading] = useState(false);

    // ─── Check for auto-login query params ───
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const email = queryParams.get('email');
        const password = queryParams.get('password');
        const autoLogin = queryParams.get('autoLogin');

        console.log('Auto-login params:', { email, password, autoLogin }); // Debug log

        if (autoLogin === 'true' && email && password && !autoLoginAttempted) {
            // Set form data
            setFormData({ email, password });
            setAutoLoginAttempted(true);
            setAutoLoginLoading(true);
            
            // Auto login after a small delay
            setTimeout(() => {
                handleAutoLogin(email, password);
            }, 500);
        } else if (autoLogin === 'true' && email && !password) {
            // Password missing - show error
            setMessage({
                text: "⚠️ Password missing for auto-login. Please enter manually.",
                type: "error"
            });
            setAutoLoginAttempted(true);
        }
    }, [location.search]);

    // ─── Auto Login Function ───
    const handleAutoLogin = async (email, password) => {
        console.log('Auto-login API call with:', { email, password }); // Debug log
        
        try {
            const res = await axios.post(`${API_BASE_URL}/candidate/login`, { email, password });
            console.log('Auto-login response:', res.data); // Debug log
            
            if (res.data.success && res.data.token) {
                setMessage({ text: "", type: "" });
                localStorage.setItem("candidateToken", res.data.token);
                localStorage.setItem("candidateId", res.data.candidate._id);
                localStorage.setItem("candidateName", res.data.candidate.name);
                localStorage.setItem("userRole", "candidate");
                
                setCandidateName(res.data.candidate.name);
                
                // Play voice message
                playVoiceMessage();
                
                // Show congratulations popup
                setShowCongratsPopup(true);
                setAutoLoginLoading(false);
                
                toast.success("Login successful! Welcome!");
                
                // Redirect after popup closes
                setTimeout(() => {
                    setShowCongratsPopup(false);
                    if (jobId) {
                        navigateWithCleanup(`/applying-job/${jobId}`);
                    } else {
                        navigateWithCleanup("/candidate-dashboard");
                    }
                }, 4000);
            } else {
                setAutoLoginLoading(false);
                setMessage({
                    text: res.data?.message || "Login failed. Please try manually.",
                    type: "error"
                });
                toast.error("Login failed. Please try manually.");
            }
        } catch (err) {
            console.error("Auto login error:", err);
            setAutoLoginLoading(false);
            setMessage({
                text: err.response?.data?.message || "Auto login failed. Please try manually.",
                type: "error"
            });
            toast.error("Auto login failed. Please try manually.");
        }
    };

    // ─── Play voice message ───
    const playVoiceMessage = (customMessage) => {
        if (!window.speechSynthesis) {
            console.log("Speech synthesis not supported");
            return;
        }

        window.speechSynthesis.cancel();

        const messageText = customMessage || "Welcome back! You have logged in successfully.";
        
        const utterance = new SpeechSynthesisUtterance(messageText);
        
        utterance.rate = 0.9;
        utterance.pitch = 1.2;
        utterance.volume = 1;
        
        const setFemaleVoice = () => {
            const voices = window.speechSynthesis.getVoices();
            const femaleVoice = voices.find(voice => 
                voice.name.includes("Google UK English Female") ||
                voice.name.includes("Samantha") ||
                voice.name.includes("Victoria") ||
                voice.name.includes("Susan") ||
                voice.name.includes("Microsoft Zira") ||
                voice.name.includes("Google US English Female") ||
                voice.name.toLowerCase().includes("female")
            );
            
            if (femaleVoice) {
                utterance.voice = femaleVoice;
            }
            window.speechSynthesis.speak(utterance);
        };
        
        const voices = window.speechSynthesis.getVoices();
        if (voices.length > 0) {
            setFemaleVoice();
        } else {
            window.speechSynthesis.onvoiceschanged = () => {
                setFemaleVoice();
            };
        }
        
        setIsVoicePlaying(true);
        
        utterance.onend = () => {
            setIsVoicePlaying(false);
        };
        
        utterance.onerror = () => {
            setIsVoicePlaying(false);
        };
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ text: "", type: "" });

        try {
            const res = await axios.post(`${API_BASE_URL}/candidate/login`, formData);
            
            if (res.data.success && res.data.token) {
                setMessage({ text: "", type: "" });
                localStorage.setItem("candidateToken", res.data.token);
                localStorage.setItem("candidateId", res.data.candidate._id);
                localStorage.setItem("candidateName", res.data.candidate.name);
                localStorage.setItem("userRole", "candidate");
                
                setCandidateName(res.data.candidate.name);
                
                playVoiceMessage();
                
                setShowCongratsPopup(true);
                
                toast.success("Login successful! Welcome!");
                
                setTimeout(() => {
                    setShowCongratsPopup(false);
                    if (jobId) {
                        navigateWithCleanup(`/applying-job/${jobId}`);
                    } else {
                        navigateWithCleanup("/candidate-dashboard");
                    }
                }, 4000);
            }
        } catch (err) {
            console.error("Login error:", err);
            setMessage({
                text: err.response?.data?.message || "Invalid email or password. Please try again.",
                type: "error"
            });
            toast.error(err.response?.data?.message || "Login failed");
        } finally {
            setLoading(false);
        }
    };

    const handleForgotEmailSubmit = async (e) => {
        e.preventDefault();
        setForgotLoading(true);
        setMessage({ text: "", type: "" });

        try {
            const res = await axios.post(`${API_BASE_URL}/candidate/forgot-password`, { email: forgotEmail });
            if (res.data.success) {
                setMessage({ text: "OTP sent to your email.", type: "success" });
                toast.success("OTP sent to your email.");
                setStep("forgot-reset");
            }
        } catch (err) {
            console.error("Forgot password error:", err);
            setMessage({
                text: err.response?.data?.message || "Failed to send reset OTP. Please verify your email.",
                type: "error"
            });
            toast.error(err.response?.data?.message || "Failed to send reset OTP");
        } finally {
            setForgotLoading(false);
        }
    };

    const handleResetPasswordSubmit = async (e) => {
        e.preventDefault();
        setForgotLoading(true);
        setMessage({ text: "", type: "" });

        try {
            const res = await axios.post(`${API_BASE_URL}/candidate/reset-password`, {
                email: forgotEmail,
                otp: resetOtp,
                newPassword
            });

            if (res.data.success) {
                toast.success("Password reset successfully!");
                playVoiceMessage("Password reset successfully. Redirecting to login page.");
                setMessage({ text: "Password reset successfully! Redirecting to login...", type: "success" });
                
                setTimeout(() => {
                    setStep("login");
                    setForgotEmail("");
                    setResetOtp("");
                    setNewPassword("");
                    setMessage({ text: "", type: "" });
                }, 3000);
            }
        } catch (err) {
            console.error("Reset password error:", err);
            setMessage({
                text: err.response?.data?.message || "Failed to reset password. Please try again.",
                type: "error"
            });
            toast.error(err.response?.data?.message || "Failed to reset password");
        } finally {
            setForgotLoading(false);
        }
    };

    // ─── Show loading screen during auto-login ───
    if (autoLoginLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6 font-sans">
                <div className="bg-white rounded-2xl shadow-xl p-12 text-center max-w-md border border-gray-100">
                    <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-16 h-16 mx-auto mb-4"
                    >
                        <FaSpinner className="w-16 h-16 text-indigo-600" />
                    </motion.div>
                    <h2 className="text-xl font-bold text-gray-800">Auto Logging In...</h2>
                    <p className="text-sm text-gray-500 mt-2">Please wait while we log you in.</p>
                    <div className="mt-4 flex justify-center gap-1">
                        <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
                        <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                    </div>
                    <p className="text-xs text-gray-400 mt-4">Logging in as: <span className="font-semibold">{formData.email}</span></p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6 font-sans">
            <div className="w-full max-w-5xl bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col md:flex-row border border-gray-200">

                {/* Visual Side */}
                <div className="md:w-1/2 bg-gradient-to-br from-indigo-600 via-blue-600 to-indigo-700 p-12 text-white flex flex-col justify-between relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.1)_0%,transparent_60%)]"></div>

                    <div className="relative z-10">
                        <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mb-10 backdrop-blur-sm border border-white/20 shadow-sm">
                            <FaTasks className="text-2xl" />
                        </div>
                        <h2 className="text-4xl font-black leading-tight mb-4">
                            Welcome back, <br />
                            <span className="text-indigo-200">
                                Future Talent
                            </span>
                        </h2>
                        <p className="text-lg text-indigo-100/80 font-medium max-w-xs leading-relaxed">
                            Access your personalized dashboard to track applications, complete assessments, and build your profile.
                        </p>
                    </div>

                    <div className="relative z-10 pt-12">
                        <div className="flex -space-x-3 mb-6">
                            {[11, 12, 13, 14].map(i => (
                                <div key={i} className="w-10 h-10 rounded-full border-2 border-indigo-600 bg-indigo-500 flex items-center justify-center overflow-hidden">
                                    <img src={`https://i.pravatar.cc/100?img=${i}`} alt="user" className="w-full h-full object-cover" />
                                </div>
                            ))}
                            <div className="w-10 h-10 rounded-full border-2 border-indigo-600 bg-indigo-700 flex items-center justify-center text-[10px] font-black">
                                +2k
                            </div>
                        </div>
                        <p className="text-[10px] font-black text-indigo-200/60 uppercase tracking-[0.2em]">
                            Joined by 2,000+ top candidates
                        </p>
                    </div>
                </div>

                {/* Form Side */}
                <div className="md:w-1/2 p-12 md:p-20 flex flex-col justify-center bg-white">
                    {step === "login" && (
                        <>
                            <div className="mb-12">
                                <h1 className="text-2xl font-black text-gray-900 mb-1">Candidate Login</h1>
                                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                                    Enter your credentials to continue
                                </p>
                                {/* Show auto-login indicator */}
                                {autoLoginAttempted && !autoLoginLoading && (
                                    <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 border border-emerald-200 rounded-full">
                                        <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></div>
                                        <span className="text-[10px] font-medium text-emerald-600">Auto-login completed</span>
                                    </div>
                                )}
                            </div>

                            {/* Message Alert */}
                            {message.text && (
                                <div className={`mb-8 p-4 rounded-xl flex items-center gap-3 animate-in slide-in-from-top-4 duration-300 border ${message.type === "success"
                                    ? "bg-emerald-50 text-emerald-600 border-emerald-100"
                                    : "bg-rose-50 text-rose-600 border-rose-100"
                                    }`}>
                                    {message.type === "success" ? <FiCheckCircle className="text-lg shrink-0" /> : <FiAlertCircle className="text-lg shrink-0" />}
                                    <span className="text-sm font-bold">{message.text}</span>
                                </div>
                            )}

                            {/* Login Form */}
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Email Address</label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-indigo-600 transition-colors">
                                            <FiMail className="text-lg" />
                                        </div>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                            className="w-full pl-11 pr-4 py-4 bg-white border border-gray-200 rounded-xl focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 outline-none transition-all text-sm font-medium text-gray-700 placeholder:text-gray-300 shadow-sm"
                                            placeholder="alex@example.com"
                                            autoComplete="email"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <div className="flex justify-between items-center ml-1">
                                        <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest">Password</label>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setStep("forgot-email");
                                                setMessage({ text: "", type: "" });
                                            }}
                                            className="text-[10px] font-black text-indigo-600 hover:text-indigo-700 hover:underline uppercase tracking-widest"
                                        >
                                            Forgot Password?
                                        </button>
                                    </div>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-indigo-600 transition-colors">
                                            <FiLock className="text-lg" />
                                        </div>
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            required
                                            className="w-full pl-11 pr-12 py-4 bg-white border border-gray-200 rounded-xl focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 outline-none transition-all text-sm font-medium text-gray-700 placeholder:text-gray-300 shadow-sm"
                                            placeholder="••••••••"
                                            autoComplete="current-password"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-indigo-600 transition-colors"
                                        >
                                            {showPassword ? <FiEyeOff className="text-lg" /> : <FiEye className="text-lg" />}
                                        </button>
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading || !formData.email || !formData.password}
                                    className="w-full py-6 bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-black rounded-xl shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 transition-all flex items-center justify-center gap-3 disabled:opacity-70 disabled:pointer-events-none group uppercase tracking-widest text-sm"
                                >
                                    {loading ? (
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    ) : (
                                        <>
                                            <span>Login</span>
                                            <FiArrowRight className="text-sm group-hover:translate-x-1 transition-transform" />
                                        </>
                                    )}
                                </button>
                            </form>

                            {/* Register Link */}
                            <p className="mt-12 text-center text-[11px] text-gray-500 font-bold uppercase tracking-wider">
                                Don't have an account?{" "}
                                <Link to={jobId ? `/email-verification/${jobId}` : "/email-verification"} className="text-indigo-600 font-black hover:underline underline-offset-8 transition-all">
                                    Create Account
                                </Link>
                            </p>
                        </>
                    )}

                    {step === "forgot-email" && (
                        <>
                            <div className="mb-12">
                                <h1 className="text-2xl font-black text-gray-900 mb-1">Forgot Password</h1>
                                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                                    Enter your email to receive reset OTP
                                </p>
                            </div>

                            {/* Message Alert */}
                            {message.text && (
                                <div className={`mb-8 p-4 rounded-xl flex items-center gap-3 animate-in slide-in-from-top-4 duration-300 border ${message.type === "success"
                                    ? "bg-emerald-50 text-emerald-600 border-emerald-100"
                                    : "bg-rose-50 text-rose-600 border-rose-100"
                                    }`}>
                                    {message.type === "success" ? <FiCheckCircle className="text-lg shrink-0" /> : <FiAlertCircle className="text-lg shrink-0" />}
                                    <span className="text-sm font-bold">{message.text}</span>
                                </div>
                            )}

                            {/* Forgot Email Form */}
                            <form onSubmit={handleForgotEmailSubmit} className="space-y-6">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Email Address</label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-indigo-600 transition-colors">
                                            <FiMail className="text-lg" />
                                        </div>
                                        <input
                                            type="email"
                                            value={forgotEmail}
                                            onChange={(e) => setForgotEmail(e.target.value)}
                                            required
                                            className="w-full pl-11 pr-4 py-4 bg-white border border-gray-200 rounded-xl focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 outline-none transition-all text-sm font-medium text-gray-700 placeholder:text-gray-300 shadow-sm"
                                            placeholder="alex@example.com"
                                            autoComplete="email"
                                        />
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={forgotLoading || !forgotEmail}
                                    className="w-full py-6 bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-black rounded-xl shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 transition-all flex items-center justify-center gap-3 disabled:opacity-70 disabled:pointer-events-none group uppercase tracking-widest text-sm"
                                >
                                    {forgotLoading ? (
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    ) : (
                                        <>
                                            <span>Send OTP</span>
                                            <FiArrowRight className="text-sm group-hover:translate-x-1 transition-transform" />
                                        </>
                                    )}
                                </button>
                            </form>

                            {/* Back to Login */}
                            <button
                                type="button"
                                onClick={() => {
                                    setStep("login");
                                    setForgotEmail("");
                                    setMessage({ text: "", type: "" });
                                }}
                                className="mt-8 w-full text-center text-[11px] text-gray-500 font-bold uppercase tracking-wider hover:text-indigo-600 transition-colors"
                            >
                                ← Back to Login
                            </button>
                        </>
                    )}

                    {step === "forgot-reset" && (
                        <>
                            <div className="mb-12">
                                <h1 className="text-2xl font-black text-gray-900 mb-1">Reset Password</h1>
                                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                                    Enter OTP and new password
                                </p>
                            </div>

                            {/* Message Alert */}
                            {message.text && (
                                <div className={`mb-8 p-4 rounded-xl flex items-center gap-3 animate-in slide-in-from-top-4 duration-300 border ${message.type === "success"
                                    ? "bg-emerald-50 text-emerald-600 border-emerald-100"
                                    : "bg-rose-50 text-rose-600 border-rose-100"
                                    }`}>
                                    {message.type === "success" ? <FiCheckCircle className="text-lg shrink-0" /> : <FiAlertCircle className="text-lg shrink-0" />}
                                    <span className="text-sm font-bold">{message.text}</span>
                                </div>
                            )}

                            {/* Reset Password Form */}
                            <form onSubmit={handleResetPasswordSubmit} className="space-y-6">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">OTP</label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-indigo-600 transition-colors">
                                            <FaKey className="text-lg" />
                                        </div>
                                        <input
                                            type="text"
                                            value={resetOtp}
                                            onChange={(e) => setResetOtp(e.target.value)}
                                            required
                                            maxLength={6}
                                            className="w-full pl-11 pr-4 py-4 bg-white border border-gray-200 rounded-xl focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 outline-none transition-all text-sm font-medium text-gray-700 placeholder:text-gray-300 shadow-sm"
                                            placeholder="Enter 6-digit OTP"
                                            autoComplete="one-time-code"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">New Password</label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-indigo-600 transition-colors">
                                            <FiLock className="text-lg" />
                                        </div>
                                        <input
                                            type={showNewPassword ? "text" : "password"}
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            required
                                            className="w-full pl-11 pr-12 py-4 bg-white border border-gray-200 rounded-xl focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 outline-none transition-all text-sm font-medium text-gray-700 placeholder:text-gray-300 shadow-sm"
                                            placeholder="••••••••"
                                            autoComplete="new-password"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowNewPassword(!showNewPassword)}
                                            className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-indigo-600 transition-colors"
                                        >
                                            {showNewPassword ? <FiEyeOff className="text-lg" /> : <FiEye className="text-lg" />}
                                        </button>
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={forgotLoading || !resetOtp || !newPassword}
                                    className="w-full py-6 bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-black rounded-xl shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 transition-all flex items-center justify-center gap-3 disabled:opacity-70 disabled:pointer-events-none group uppercase tracking-widest text-sm"
                                >
                                    {forgotLoading ? (
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    ) : (
                                        <>
                                            <span>Reset Password</span>
                                            <FiArrowRight className="text-sm group-hover:translate-x-1 transition-transform" />
                                        </>
                                    )}
                                </button>
                            </form>

                            {/* Back to Email */}
                            <button
                                type="button"
                                onClick={() => {
                                    setStep("forgot-email");
                                    setResetOtp("");
                                    setNewPassword("");
                                    setMessage({ text: "", type: "" });
                                }}
                                className="mt-8 w-full text-center text-[11px] text-gray-500 font-bold uppercase tracking-wider hover:text-indigo-600 transition-colors"
                            >
                                ← Back to Email
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Congratulations Popup with Voice Indicator */}
            <AnimatePresence>
                {showCongratsPopup && (
                    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 30 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 30 }}
                            transition={{ type: "spring", damping: 20, stiffness: 300 }}
                            className="relative bg-white w-full max-w-md rounded-[2rem] shadow-2xl border border-slate-100 overflow-hidden"
                        >
                            {/* Voice Playing Indicator */}
                            {isVoicePlaying && (
                                <div className="absolute top-4 right-4 z-20">
                                    <motion.div
                                        animate={{ scale: [1, 1.08, 1] }}
                                        transition={{ duration: 0.6, repeat: Infinity }}
                                        className="flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 px-2.5 py-1 rounded-full text-[9px] font-bold shadow-sm"
                                    >
                                        <FaVolumeUp size={10} className="animate-pulse" />
                                        <span>Playing Voice...</span>
                                    </motion.div>
                                </div>
                            )}

                            {/* Confetti Animation Background */}
                            <div className="absolute inset-0 pointer-events-none">
                                {[...Array(20)].map((_, i) => {
                                    const colors = ['#ef4444', '#10b981', '#3b82f6', '#eab308', '#8b5cf6', '#ec4899'];
                                    const randColor = colors[Math.floor(Math.random() * colors.length)];
                                    return (
                                        <motion.div
                                            key={i}
                                            initial={{ 
                                                y: -100, 
                                                x: Math.random() * 400 - 200,
                                                rotate: 0,
                                                opacity: 1
                                            }}
                                            animate={{ 
                                                y: 600,
                                                x: Math.random() * 400 - 200,
                                                rotate: 360,
                                                opacity: 0
                                            }}
                                            transition={{ 
                                                duration: 2, 
                                                delay: Math.random() * 0.5,
                                                repeat: Infinity,
                                                repeatDelay: Math.random() * 2
                                            }}
                                            className="absolute"
                                        >
                                            <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: randColor }} />
                                        </motion.div>
                                    );
                                })}
                            </div>

                            {/* Header */}
                            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-8 py-8 text-center relative">
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ type: "spring", delay: 0.2 }}
                                    className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center mx-auto mb-4 border-4 border-white/30"
                                >
                                    <FaTrophy className="text-4xl text-yellow-300" />
                                </motion.div>
                                
                                <motion.h2
                                    initial={{ y: 20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ delay: 0.3 }}
                                    className="text-xl font-extrabold text-white tracking-wide uppercase"
                                >
                                    Welcome Back!
                                </motion.h2>
                                
                                <motion.p
                                    initial={{ y: 20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ delay: 0.4 }}
                                    className="text-white/80 text-xs mt-1 font-medium"
                                >
                                    You have logged in successfully
                                </motion.p>
                            </div>

                            {/* Content */}
                            <div className="p-8 text-center">
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ delay: 0.5, type: "spring" }}
                                    className="flex items-center justify-center gap-2 mb-4"
                                >
                                    <FaCheckCircle className="text-emerald-500 text-xl" />
                                    <span className="text-emerald-600 font-bold text-sm">Login Successful</span>
                                </motion.div>

                                <motion.h3
                                    initial={{ y: 20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ delay: 0.6 }}
                                    className="text-base font-bold text-gray-800"
                                >
                                    Welcome back, <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-indigo-600">{candidateName || "Candidate"}!</span>
                                </motion.h3>

                                <motion.p
                                    initial={{ y: 20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ delay: 0.7 }}
                                    className="text-[11px] text-gray-500 mt-2 font-medium"
                                >
                                    Redirecting to your dashboard in a few seconds...
                                </motion.p>

                                {/* Voice Message Text */}
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.8 }}
                                    className="mt-5 p-3 bg-emerald-50/50 rounded-xl border border-emerald-100/50"
                                >
                                    <p className="text-[10px] text-emerald-600 font-semibold flex items-center justify-center gap-2">
                                        <FaVolumeUp className="shrink-0" />
                                        "Welcome back! You have logged in successfully."
                                    </p>
                                </motion.div>

                                {/* Progress Bar */}
                                <motion.div
                                    initial={{ width: "100%" }}
                                    animate={{ width: "0%" }}
                                    transition={{ duration: 3.5, ease: "linear" }}
                                    className="h-1 bg-gradient-to-r from-emerald-500 to-indigo-500 rounded-full mt-6"
                                />

                                {/* Stars Animation */}
                                <div className="flex justify-center gap-1.5 mt-5">
                                    {[1, 2, 3, 4, 5].map((star, i) => (
                                        <motion.div
                                            key={star}
                                            initial={{ scale: 0, opacity: 0 }}
                                            animate={{ scale: 1, opacity: 1 }}
                                            transition={{ delay: 0.9 + (i * 0.1) }}
                                        >
                                            <FaStar className="text-amber-400 text-sm" />
                                        </motion.div>
                                    ))}
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="px-8 py-4 bg-slate-50 border-t border-slate-100 flex justify-center items-center gap-2">
                                <motion.div
                                    animate={{ 
                                        scale: [1, 1.15, 1],
                                        rotate: [0, 8, -8, 0]
                                    }}
                                    transition={{ duration: 0.6, repeat: Infinity, repeatDelay: 1 }}
                                >
                                    <FaFire className="text-orange-500 text-sm" />
                                </motion.div>
                                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">
                                    Ready to explore opportunities!
                                </span>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default CandidateLogin;